import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTypedOpenApi } from '../../providers';
import { indexFavorites } from './favoriteIndex';
import { favoritesQueryOptions } from './favoriteQueries';
import { BOAT, type FavoritableType } from './favoritesApi';

export type UseFavoritesQueryOptions = {
  favoritableType?: FavoritableType;
  /**
   * Pass `isSignedIn`. Left `true` for a signed-out visitor, every boat list page would issue a
   * request that can only fail — which is exactly what the Strapi implementation this replaces did.
   */
  enabled?: boolean;
};

/**
 * The caller's favorites, plus the two lookups a boat card needs.
 *
 * One request and one index for a whole page of cards, rather than per-card state — which is why
 * the endpoint is unpaginated and why the query key does not mention the screen that asked.
 */
export const useFavoritesQuery = ({ favoritableType = BOAT, enabled = true }: UseFavoritesQueryOptions = {}) => {
  const { apiClient } = useTypedOpenApi();

  const queryInfo = useQuery(favoritesQueryOptions(apiClient, { favoritableType, enabled }));

  const { data } = queryInfo;

  // Memoized on the data it reads, so a page of cards does not rebuild the map per card and the
  // callbacks only change identity when the favorites actually change.
  const { isFavorite, favoriteUuidOf } = useMemo(() => indexFavorites(data), [data]);

  return { ...queryInfo, isFavorite, favoriteUuidOf };
};
